import { Plus, Trash2 } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import {
  emptySubStep,
  MAX_SUB_STEPS,
  type WorkflowFormInput,
} from '../schemas/workflow-form-schema';

import { DurationField } from './duration-field';

export interface SubStepsEditorProps {
  stageIndex: number;
  disabled?: boolean;
}

/** Parallel steps of a task stage: each has a name, its own duration and may be optional. */
export function SubStepsEditor({ stageIndex, disabled = false }: SubStepsEditorProps) {
  const { t } = useTranslation('workflows');
  const form = useFormContext<WorkflowFormInput>();
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: `stages.${stageIndex}.subSteps`,
  });
  const subSteps = useWatch({ control: form.control, name: `stages.${stageIndex}.subSteps` });
  const names = subSteps.map((sub) => (sub.name.trim() === '' ? undefined : sub.name.trim()));

  return (
    <fieldset className="flex flex-col gap-3 rounded-md border p-3">
      <legend className="px-1 text-sm font-medium">{t('subSteps.title')}</legend>
      <p className="text-muted-foreground text-xs">{t('subSteps.description')}</p>
      <ol className="flex flex-col gap-3" aria-label={t('subSteps.title')}>
        {fields.map((item, index) => {
          const prefix = `stages.${stageIndex}.subSteps.${index}` as const;
          const name = names[index] ?? t('subSteps.label', { index: index + 1 });
          return (
            <li
              key={item.id}
              aria-label={t('subSteps.label', { index: index + 1 })}
              className="bg-muted/30 flex flex-col gap-3 rounded-md border p-3"
            >
              <div className="flex items-start gap-2">
                <FormField
                  control={form.control}
                  name={`${prefix}.name`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>{t('subSteps.name')}</FormLabel>
                      <FormControl>
                        <Input {...field} maxLength={120} disabled={disabled} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="mt-6"
                  aria-label={t('subSteps.remove', { name })}
                  disabled={disabled}
                  onClick={() => remove(index)}
                >
                  <Trash2 aria-hidden />
                </Button>
              </div>
              <FormField
                control={form.control}
                name={`${prefix}.key`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('subSteps.key')}</FormLabel>
                    <FormControl>
                      <Input {...field} className="font-mono" maxLength={60} disabled={disabled} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DurationField
                label={t('stage.duration')}
                valueName={`${prefix}.durationValue`}
                unitName={`${prefix}.durationUnit`}
                disabled={disabled}
              />
              <FormField
                control={form.control}
                name={`${prefix}.optional`}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={disabled}
                      />
                    </FormControl>
                    <FormLabel className="font-normal">{t('subSteps.optional')}</FormLabel>
                  </FormItem>
                )}
              />
            </li>
          );
        })}
      </ol>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={disabled || fields.length >= MAX_SUB_STEPS}
        onClick={() => append(emptySubStep())}
      >
        <Plus aria-hidden />
        {t('subSteps.add')}
      </Button>
    </fieldset>
  );
}
